import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { IMAGES } from "@/lib/api";

/**
 * Cinematic on-load moment:
 * 1. Warm-white screen, the Hue logo as a faint outline.
 * 2. A coastal interior gently washes through the logo's negative space, filling the mark.
 * 3. The screen fades away softly to reveal the hero.
 */
export default function Preloader({ onComplete }) {
  const firedRef = useRef(false);
  const [size] = useState(() => {
    if (typeof window === "undefined") return 400;
    return Math.min(window.innerWidth * 0.6, 420);
  });

  const finish = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onComplete && onComplete();
  };

  useEffect(() => {
    const t = setTimeout(finish, 3300);
    return () => clearTimeout(t);
  }, []);

  const mask = {
    WebkitMaskImage: `url(${IMAGES.logo})`,
    maskImage: `url(${IMAGES.logo})`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: `${size}px`,
    maskSize: `${size}px`,
  };

  return (
    <motion.div
      data-testid="intro-preloader"
      className="fixed inset-0 z-[100] overflow-hidden"
      style={{ backgroundColor: "#F5F3EE" }}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } }}
    >
      {/* Faint outline for the opening beat */}
      <motion.img
        src={IMAGES.logo}
        alt="Hue Creative"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none pointer-events-none"
        style={{ width: size, filter: "grayscale(1)" }}
        initial={{ opacity: 0.14 }}
        animate={{ opacity: [0.14, 0.14, 0] }}
        transition={{ duration: 2.6, times: [0, 0.6, 0.85], ease: "linear" }}
      />

      {/* Coastal interior washing through the logo's negative space, then holding */}
      <motion.div
        className="absolute inset-0"
        style={mask}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
          initial={{ scale: 1.05, x: "-5%" }}
          animate={{ scale: 1, x: "0%" }}
          transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
          onAnimationComplete={finish}
        />
      </motion.div>
    </motion.div>
  );
}
